import { Fragment, useRef, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  FormText,
  Input,
  Label,
  Row,
} from "reactstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { GetSupportRequestCategories } from "../../actions/teacherQueries";
import { CreateSupportRequest } from "../../actions/teacherMutations";
import { formatServiceError } from "../../Utilities/serviceErrorHelper";

const MAX_BODY_LENGTH = 8000;
const MAX_TOTAL_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const ALLOWED_EXTENSIONS = [".png", ".jpg", ".jpeg"];

const CATEGORY_DISPLAY = {
  BugReport: {
    icon: "circle-exclamation",
    placeholder: "ContactSupportMessagePlaceholderBugReport",
  },
  Support: {
    icon: "circle-question",
    placeholder: "ContactSupportMessagePlaceholderSupport",
  },
  Feedback: {
    icon: "lightbulb",
    placeholder: "ContactSupportMessagePlaceholderFeedback",
  },
  Apl: {
    icon: "briefcase",
    placeholder: "ContactSupportMessagePlaceholderApl",
  },
};

export default function ContactSupportForm() {
  const intl = useIntl();
  const categories = GetSupportRequestCategories();
  const [category, setCategory] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [showErrors, setShowErrors] = useState(false);
  const fileInput = useRef(null);

  const mutateSupportRequest = useMutation({
    mutationFn: CreateSupportRequest,
  });

  const missingCategory = !category;
  const missingSubject = !subject.trim();
  const missingBody = !body.trim();

  const handleFilesChosen = (event) => {
    const chosenFiles = Array.from(event.target.files);
    event.target.value = "";

    if (chosenFiles.length === 0) return;

    const combinedFiles = [...attachments, ...chosenFiles];

    const totalBytes = combinedFiles.reduce((sum, file) => sum + file.size, 0);

    if (totalBytes > MAX_TOTAL_ATTACHMENT_BYTES) {
      toast.warn(<FormattedMessage id="TicketAttachmentsTooLarge" />);
      return;
    }

    setAttachments(combinedFiles);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (missingCategory || missingSubject || missingBody) {
      setShowErrors(true);
      return;
    }

    mutateSupportRequest.mutate(
      {
        category,
        subject,
        body,
        attachments,
      },
      {
        onSuccess: () => {
          toast.success(<FormattedMessage id="ContactSupportSuccess" />);
          setCategory("");
          setSubject("");
          setBody("");
          setAttachments([]);
          setShowErrors(false);
        },
        onError: (err) => {
          toast.warn(formatServiceError(intl, err?.response?.data));
        },
      },
    );
  };

  if (categories.isPending || !categories.data?.length) return "";

  return (
    <Card>
      <CardBody>
        <CardTitle>
          <FormattedMessage id="ContactSupportTitle" />
          <hr />
        </CardTitle>

        <Row>
          <Col lg={9} xxl={7}>
            <Form onSubmit={handleSubmit}>
              <FormGroup className="mb-4">
                <Label className="fw-semibold d-block">
                  <FormattedMessage id="ContactSupportCategory" />
                </Label>
                <div className="d-flex flex-wrap gap-2">
                  {categories.data.map((categoryName) => {
                    const isSelected = category === categoryName;

                    return (
                      <Fragment key={categoryName}>
                        <input
                          type="radio"
                          className="btn-check"
                          name="supportRequestCategory"
                          id={`supportRequestCategory-${categoryName}`}
                          autoComplete="off"
                          checked={isSelected}
                          onChange={() => setCategory(categoryName)}
                        />
                        <label
                          className={`btn btn-sm d-flex align-items-center gap-2${
                            isSelected ? " btn-outline-primary" : ""
                          }`}
                          style={
                            isSelected
                              ? {
                                  "--bs-btn-active-bg": "transparent",
                                  "--bs-btn-active-color": "var(--bs-primary)",
                                }
                              : {
                                  "--bs-btn-border-color":
                                    "var(--bs-border-color)",
                                  "--bs-btn-hover-bg": "var(--bs-tertiary-bg)",
                                  "--bs-btn-hover-border-color":
                                    "var(--bs-border-color)",
                                }
                          }
                          htmlFor={`supportRequestCategory-${categoryName}`}
                        >
                          <FontAwesomeIcon
                            icon={
                              CATEGORY_DISPLAY[categoryName]?.icon ?? "comment"
                            }
                            className={isSelected ? undefined : "text-muted"}
                          />
                          {CATEGORY_DISPLAY[categoryName]
                            ? intl.formatMessage({
                                id: `SupportCategory${categoryName}`,
                              })
                            : categoryName}
                        </label>
                      </Fragment>
                    );
                  })}
                </div>
                {showErrors && missingCategory ? (
                  <FormFeedback className="d-block">
                    <FormattedMessage id="InvalidTicketCategory" />
                  </FormFeedback>
                ) : (
                  ""
                )}
              </FormGroup>

              <FormGroup className="mb-4">
                <Label className="fw-semibold" for="supportRequestSubject">
                  <FormattedMessage id="ContactSupportSubject" />
                </Label>
                <Input
                  id="supportRequestSubject"
                  maxLength={200}
                  value={subject}
                  invalid={showErrors && missingSubject}
                  onChange={(event) => setSubject(event.target.value)}
                />
                <FormFeedback>
                  <FormattedMessage id="ContactSupportSubjectRequired" />
                </FormFeedback>
              </FormGroup>

              <FormGroup className="mb-4">
                <Label className="fw-semibold" for="supportRequestBody">
                  <FormattedMessage id="ContactSupportMessage" />
                </Label>
                <Input
                  type="textarea"
                  id="supportRequestBody"
                  rows={10}
                  maxLength={MAX_BODY_LENGTH}
                  value={body}
                  placeholder={
                    CATEGORY_DISPLAY[category]
                      ? intl.formatMessage({
                          id: CATEGORY_DISPLAY[category].placeholder,
                        })
                      : ""
                  }
                  invalid={showErrors && missingBody}
                  onChange={(event) => setBody(event.target.value)}
                />
                <FormFeedback>
                  <FormattedMessage id="ContactSupportBodyRequired" />
                </FormFeedback>
                <FormText className="d-block text-end">
                  {body.length} / {MAX_BODY_LENGTH}
                </FormText>
              </FormGroup>

              <FormGroup className="mb-4">
                <Label
                  className="fw-semibold d-block"
                  for="supportRequestAttachments"
                >
                  <FormattedMessage id="ContactSupportAttachments" />{" "}
                  <span className="fw-normal text-muted">
                    · <FormattedMessage id="ContactSupportOptional" />
                  </span>
                </Label>

                <Input
                  innerRef={fileInput}
                  type="file"
                  id="supportRequestAttachments"
                  className="d-none"
                  accept={ALLOWED_EXTENSIONS.join(",")}
                  multiple
                  onChange={handleFilesChosen}
                />
                <Button
                  type="button"
                  color="primary"
                  outline
                  onClick={() => fileInput.current.click()}
                >
                  <FontAwesomeIcon icon="paperclip" className="me-2" />
                  <FormattedMessage id="ContactSupportChooseFiles" />
                </Button>

                {attachments.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className="d-flex align-items-center mt-2"
                  >
                    <FontAwesomeIcon icon="image" className="text-muted me-2" />
                    <span className="me-2">{file.name}</span>
                    <span className="text-muted me-2">
                      {Math.round(file.size / 1024)} kB
                    </span>
                    <Button
                      type="button"
                      color="link"
                      size="sm"
                      className="p-0 text-danger"
                      title={intl.formatMessage({
                        id: "ContactSupportRemoveAttachment",
                      })}
                      aria-label={intl.formatMessage({
                        id: "ContactSupportRemoveAttachment",
                      })}
                      onClick={() =>
                        setAttachments(
                          attachments.filter(
                            (_, position) => position !== index,
                          ),
                        )
                      }
                    >
                      <FontAwesomeIcon icon="xmark" />
                    </Button>
                  </div>
                ))}
              </FormGroup>

              <hr />

              <div className="d-flex align-items-center justify-content-end flex-wrap gap-2">
                <Button
                  type="submit"
                  color="primary"
                  disabled={mutateSupportRequest.isPending}
                >
                  {mutateSupportRequest.isPending ? (
                    <FormattedMessage id="ContactSupportSending" />
                  ) : (
                    <FormattedMessage id="ContactSupportSubmit" />
                  )}
                </Button>
              </div>
            </Form>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
}
